import * as React from "react"
import * as CollapsiblePrimitive from "@radix-ui/react-collapsible"
import { ChevronUp } from "@carbon/icons-react"
import { cn } from "../../../lib/utils"

const Collapsible = CollapsiblePrimitive.Root

const CollapsibleTrigger = CollapsiblePrimitive.Trigger

const CollapsibleContent = React.forwardRef<
	React.ElementRef<typeof CollapsiblePrimitive.Content>,
	React.ComponentPropsWithoutRef<typeof CollapsiblePrimitive.Content>
>(({ className, children, ...props }, ref) => (
	<CollapsiblePrimitive.Content
		ref={ref}
		className={cn(
			"overflow-hidden",
			"data-[state=open]:animate-in data-[state=open]:fade-in-0",
			"data-[state=closed]:animate-out data-[state=closed]:fade-out-0",
			className
		)}
		{...props}
	>
		{children}
	</CollapsiblePrimitive.Content>
))
CollapsibleContent.displayName = CollapsiblePrimitive.Content.displayName

export interface CollapsibleSectionProps {
	title: string
	icon?: React.ReactNode
	defaultOpen?: boolean
	open?: boolean
	onOpenChange?: (open: boolean) => void
	children: React.ReactNode
	className?: string
	headerClassName?: string
	contentClassName?: string
}

const CollapsibleSection = ({
	title,
	icon,
	defaultOpen = true,
	open,
	onOpenChange,
	children,
	className,
	headerClassName,
	contentClassName,
}: CollapsibleSectionProps) => {
	const isControlled = open !== undefined
	const [internalOpen, setInternalOpen] = React.useState(defaultOpen)
	const isOpen = isControlled ? open : internalOpen

	const handleOpenChange = (next: boolean) => {
		if (!isControlled) setInternalOpen(next)
		onOpenChange?.(next)
	}

	return (
		<Collapsible open={isOpen} onOpenChange={handleOpenChange} className={cn("w-full border-b border-border", className)}>
			<CollapsibleTrigger asChild>
				<button
					type="button"
					className={cn(
						"flex w-full items-center justify-between px-4 py-2.5",
						"font-mono text-[0.625rem] leading-none font-medium tracking-widest uppercase text-muted-foreground",
						"hover:bg-muted/40 transition-colors cursor-pointer select-none",
						headerClassName
					)}
				>
					<span className="flex items-center gap-2">
						{icon && <span className="flex items-center text-epic-neutral-11">{icon}</span>}
						{title}
					</span>
					<ChevronUp
						size={16}
						className={cn(
							"text-epic-neutral-11 transition-transform duration-200",
							!isOpen && "rotate-180"
						)}
					/>
				</button>
			</CollapsibleTrigger>
			<CollapsibleContent className={cn("pb-2", contentClassName)}>
				{children}
			</CollapsibleContent>
		</Collapsible>
	)
}
CollapsibleSection.displayName = "CollapsibleSection"

export { Collapsible, CollapsibleTrigger, CollapsibleContent, CollapsibleSection }
